import { useQuery } from '@tanstack/react-query';

import { api } from '../../services/api/client';

interface PhotoStorageAdminPanelProps {
  groupId: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PhotoStorageAdminPanel({ groupId }: PhotoStorageAdminPanelProps) {
  const usageQuery = useQuery({
    queryKey: ['photo-storage-usage', groupId],
    queryFn: () => api.photoStorageUsage(groupId),
    staleTime: 30_000,
  });
  const usage = usageQuery.data;
  const usedRatio =
    usage === undefined || usage.freeStorageBytes === 0
      ? 0
      : Math.min(1, usage.kvStorageBytes / usage.freeStorageBytes);

  return (
    <section className="photo-storage-admin" aria-labelledby="photo-storage-admin-title">
      <div className="photo-storage-admin__heading">
        <div>
          <span className="step-label">Nur für RT</span>
          <h3 id="photo-storage-admin-title">KV-Fotospeicher</h3>
        </div>
        <button
          className="button button--text"
          type="button"
          disabled={usageQuery.isFetching}
          onClick={() => void usageQuery.refetch()}
        >
          {usageQuery.isFetching ? 'Wird gelesen …' : 'Aktualisieren'}
        </button>
      </div>
      {usageQuery.isPending ? <p className="settings-hint">Speicherverbrauch wird gelesen …</p> : null}
      {usageQuery.isError ? (
        <p className="form-error" role="alert">Die Speicherübersicht konnte gerade nicht geladen werden.</p>
      ) : null}
      {usage !== undefined ? (
        <>
          <div className="photo-storage-admin__metrics">
            <strong>{formatBytes(usage.kvStorageBytes)}</strong>
            <span>von 1 GB KV-Speicher belegt</span>
            <span>{usage.kvPhotoCount} gespeicherte Fotos</span>
          </div>
          <progress
            className="photo-storage-admin__progress"
            max={1}
            value={usedRatio}
            aria-label={`${formatBytes(usage.kvStorageBytes)} von 1 GB KV-Speicher belegt`}
          />
          <p className="settings-hint">
            Die Anzeige zählt die Fotoobjekte im KV-Namespace. Es gibt keinen automatischen Upload-Stopp;
            jedes Foto darf höchstens {formatBytes(usage.maxPhotoBytes)} groß sein.
          </p>
        </>
      ) : null}
    </section>
  );
}
